import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Calendar,
  Trash2,
  Plus,
  Save,
} from "lucide-react";

export const TourDatesManagement = () => {
  const { token } = useAuth();
  const [tourDates, setTourDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ city: "", state: "", country: "USA", start_date: "", end_date: "", notes: "" });

  const fetchTourDates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/tour-dates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTourDates(response.data);
    } catch (error) {
      toast.error("Failed to load tour dates");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTourDates();
  }, [fetchTourDates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/tour-dates`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Tour date added!");
      setShowForm(false);
      setFormData({ city: "", state: "", country: "USA", start_date: "", end_date: "", notes: "" });
      fetchTourDates();
    } catch (error) {
      toast.error("Failed to add tour date");
    }
  };

  const deleteTourDate = async (id) => {
    try {
      await axios.delete(`${API}/admin/tour-dates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Tour date deleted");
      fetchTourDates();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-tour-dates">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-charcoal">Tour Schedule</h1>
          <p className="font-body text-stone-grey">Manage your travel schedule</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> Add Date
        </Button>
      </div>

      {showForm && (
        <div className="glass rounded-xl p-6 mb-8">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input placeholder="City" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} required className="form-input" />
            <Input placeholder="State" value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} className="form-input" />
            <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} required className="form-input" />
            <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} required className="form-input" />
            <Input placeholder="Notes" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="form-input md:col-span-2" />
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit" className="btn-primary">Save Tour Date</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : tourDates.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <Calendar className="w-12 h-12 text-taupe mx-auto mb-4" />
          <p className="font-body text-stone-grey">No tour dates scheduled</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tourDates.map((tour) => (
            <div key={tour.id} className="glass rounded-xl p-4 flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg text-charcoal">{tour.city}, {tour.state || tour.country}</h3>
                <p className="font-body text-sm text-taupe">{tour.start_date} - {tour.end_date}</p>
                {tour.notes && <p className="font-body text-sm text-stone-grey">{tour.notes}</p>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => deleteTourDate(tour.id)}>
                <Trash2 className="w-4 h-4 text-lipstick-red" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

